import * as Sentry from '@sentry/react'
import type { ReactElement, ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactElement | Sentry.FallbackRender
  showDialog?: boolean
}

/**
 * Sentry Error Boundary component to catch React component errors
 */
export function SentryErrorBoundary({
  children,
  fallback,
  showDialog = false,
}: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={fallback ?? <ErrorFallback />}
      showDialog={showDialog}
      beforeCapture={(scope, errorInfo) => {
        // Add additional context before capturing
        scope.setTag('errorBoundary', 'true')
        if (errorInfo && typeof errorInfo === 'object' && 'componentStack' in errorInfo) {
          scope.setContext('reactErrorInfo', {
            componentStack: String(errorInfo.componentStack),
          })
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

/**
 * Default error fallback UI
 */
function ErrorFallback() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Something went wrong</h1>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        We've been notified and are working on fixing this issue.
      </p>
      <button
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '1rem',
        }}
        onClick={() => window.location.reload()}
      >
        Reload Page
      </button>
    </div>
  )
}
